<template>
  <modal
    :loading="loading"
    :title="modalTitle"
    style="max-width: 640px"
    @close_modal="close_modal"
  >
    <template v-slot:content>
      <p v-if="!loading && jobs.length === 0" class="text-body-2 mb-0">
        {{ $t('myassets.jobs.empty') }}
      </p>

      <template v-else>
        <p class="text-caption text-medium-emphasis mb-4">
          {{ summaryText }}
        </p>

        <section v-if="runningJobs.length > 0" class="mb-4">
          <div class="text-overline mb-2">{{ $t('myassets.jobs.running') }}</div>
          <job-row
            v-for="job in visibleRunningJobs"
            :key="'running-' + job.id"
            :job="job"
            :category="category"
            :asset-previews="assetPreviews"
            :model-name="modelNameForJob(job)"
            :subtitle="subtitleForJob(job)"
          />
          <p v-if="hiddenRunningCount > 0" class="text-caption text-medium-emphasis mb-0 mt-2">
            {{ $t('myassets.jobs.and_more_running', { count: hiddenRunningCount }) }}
          </p>
        </section>

        <section v-if="queuedJobs.length > 0">
          <div class="text-overline mb-2">{{ $t('myassets.jobs.queued') }}</div>
          <job-row
            v-for="job in visibleQueuedJobs"
            :key="'queued-' + job.id"
            :job="job"
            :category="category"
            :asset-previews="assetPreviews"
            :model-name="modelNameForJob(job)"
            :subtitle="subtitleForJob(job)"
            queued
          />
          <p v-if="hiddenQueuedCount > 0" class="text-caption text-medium-emphasis mb-0 mt-2">
            {{ $t('myassets.jobs.and_more_queued', { count: hiddenQueuedCount }) }}
          </p>
        </section>
      </template>
    </template>

    <template v-slot:buttons>
      <v-btn small text color="primary" @click="refresh">
        <v-icon small class="mr-1">fas fa-sync-alt</v-icon>
        {{ $t('auto_t.btn_refresh') }}
      </v-btn>
    </template>
  </modal>
</template>

<script>
import Modal from '@/components/Modal';
import JobRow from '@/components/myassets/MyAssetsJobRow.vue';
import {
  MY_ASSETS_JOB_DISPLAY_LIMIT,
  assetCountForJob,
  collectAssetIdsFromJobs,
  isQueuedJob,
  isRunningJob,
  parseJobDescription,
} from '@/components/myassets/myAssetsJobUtils';

export default {
  name: 'MyAssetsJobsModal',
  components: { Modal, JobRow },
  props: {
    category: {
      type: String,
      required: true,
      validator: (value) => ['run_model', 'llm'].includes(value),
    },
  },
  data() {
    return {
      manualRefreshLoading: false,
      assetPreviews: {},
    };
  },
  computed: {
    modalTitle() {
      return this.category === 'run_model'
        ? this.$t('myassets.jobs.run_model_title')
        : this.$t('myassets.jobs.llm_title');
    },
    jobs() {
      return this.category === 'run_model'
        ? this.$store.getters['project_live_jobs/run_model_jobs']
        : this.$store.getters['project_live_jobs/llm_jobs'];
    },
    loading() {
      return this.manualRefreshLoading;
    },
    runningJobs() {
      return this.jobs.filter(isRunningJob);
    },
    queuedJobs() {
      return this.jobs.filter(isQueuedJob);
    },
    visibleRunningJobs() {
      return this.runningJobs.slice(0, MY_ASSETS_JOB_DISPLAY_LIMIT);
    },
    visibleQueuedJobs() {
      return this.queuedJobs.slice(0, MY_ASSETS_JOB_DISPLAY_LIMIT);
    },
    hiddenRunningCount() {
      return Math.max(0, this.runningJobs.length - MY_ASSETS_JOB_DISPLAY_LIMIT);
    },
    hiddenQueuedCount() {
      return Math.max(0, this.queuedJobs.length - MY_ASSETS_JOB_DISPLAY_LIMIT);
    },
    summaryText() {
      return this.$t('myassets.jobs.summary', {
        running: this.runningJobs.length,
        queued: this.queuedJobs.length,
      });
    },
    modelsById() {
      const models = this.$store.getters['model/get_items'] || [];
      const byId = {};
      models.forEach((model) => {
        byId[model.id] = model.name;
      });
      return byId;
    },
  },
  watch: {
    jobs: {
      immediate: true,
      handler() {
        this.loadAssetPreviews();
      },
    },
  },
  async mounted() {
    await this.$store.dispatch('project_live_jobs/ensure_subscribed');
  },
  methods: {
    close_modal() {
      this.$emit('close_modal');
    },
    async refresh() {
      this.manualRefreshLoading = true;
      try {
        await this.$store.dispatch('project_live_jobs/fetch_active');
      } catch (error) {
        console.error('Failed to load My Assets jobs', error);
      } finally {
        this.manualRefreshLoading = false;
      }
    },
    async loadAssetPreviews() {
      const assetIds = collectAssetIdsFromJobs(this.jobs, 24);
      const previews = { ...this.assetPreviews };
      const missingIds = assetIds.filter((id) => !previews[id]);
      await Promise.all(
        missingIds.map(async (assetId) => {
          try {
            const asset = await this.$api.asset.get(assetId);
            if (asset && asset.static_path) {
              const token = localStorage.token;
              const separator = asset.static_path.includes('?') ? '&' : '?';
              previews[assetId] = this.$api_host + '/' + asset.static_path + separator + 'token=' + encodeURIComponent(token);
            }
          } catch (_) {
            previews[assetId] = null;
          }
        })
      );
      this.assetPreviews = previews;
    },
    modelNameForJob(job) {
      if (this.category !== 'run_model') {
        return this.llmTitleForJob(job);
      }
      const description = parseJobDescription(job);
      if (description.model_id && this.modelsById[description.model_id]) {
        return this.modelsById[description.model_id];
      }
      if (description.model_id) {
        return this.$t('myassets.jobs.model_number', { id: description.model_id });
      }
      return this.$t('myassets.jobs.run_model');
    },
    llmTitleForJob(job) {
      const description = parseJobDescription(job);
      if (job.task === 'llm_analyse') {
        return description.display_name || this.$t('myassets.jobs.llm_preparing');
      }
      if (job.task === 'sam3_analyse') {
        const meta = description.auto_analyse || description.llm_analyse || {};
        if (meta.find_query) {
          return meta.find_query;
        }
        if (meta.llm_question) {
          const q = String(meta.llm_question);
          return q.length > 80 ? `${q.slice(0, 80)}…` : q;
        }
        const categoryName = meta.category_label_name;
        if (categoryName) {
          return categoryName;
        }
        return this.$t('myassets.jobs.llm_analyse');
      }
      const categoryName = description.llm_analyse?.category_label_name;
      if (categoryName) {
        return categoryName;
      }
      return this.$t('myassets.jobs.llm_analyse');
    },
    subtitleForJob(job) {
      if (job.task === 'llm_analyse') {
        return this.$t('myassets.jobs.llm_preparing_hint');
      }

      const description = parseJobDescription(job);
      const assetCount = assetCountForJob(job);

      if (this.category === 'run_model') {
        if (assetCount === 1) {
          return this.$t('myassets.jobs.single_asset');
        }
        return this.$t('myassets.jobs.asset_batch', { count: assetCount });
      }

      const parts = [];
      if (assetCount === 1) {
        parts.push(this.$t('myassets.jobs.single_asset'));
      } else if (assetCount > 1) {
        parts.push(this.$t('myassets.jobs.asset_batch', { count: assetCount }));
      }

      const llmMeta = description.auto_analyse || description.llm_analyse || {};
      const steps = [];
      if (llmMeta.do_find) {
        steps.push(this.$t('llm_analyse.task_find'));
      }
      if (llmMeta.do_verify) {
        steps.push(this.$t('llm_analyse.task_verify'));
      }
      if (llmMeta.do_llm_prompt_boxes) {
        steps.push(this.$t('llm_analyse.task_llm_prompt_boxes'));
      }
      if (steps.length > 0) {
        parts.push(steps.join(' · '));
      }
      if (llmMeta.find_query) {
        parts.push(llmMeta.find_query);
      }
      if (llmMeta.llm_question) {
        const qq = String(llmMeta.llm_question);
        parts.push(qq.length > 60 ? `${qq.slice(0, 60)}…` : qq);
      }

      return parts.join(' · ') || this.$t('myassets.jobs.waiting');
    },
  },
};
</script>
